import apiConfig from "app/api/apiConfig.json";
import { HolidayDraft, HolidayRecord } from "app/shared/model/Holiday";
import { getLocalApi } from "./localApi";
import { ApiErrors } from "./ApiErrors";
import { getSelectedDivisionId } from "app/shared/division/selectedDivisionStorage";

type SchoolIdentifierInput = string | number | null | undefined;

type NullableString = string | null | undefined;

const toSchoolEntityId = (value: SchoolIdentifierInput): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = String(value).trim();
    if (!parsed) {
        return null;
    }
    const match = /^school-(.+)$/i.exec(parsed);
    if (match) {
        const identifier = match[1]?.trim();
        return identifier ? `School-${identifier}` : null;
    }
    return parsed.startsWith('School-') ? parsed : `School-${parsed}`;
};

const normalizeSchoolIds = (values: readonly SchoolIdentifierInput[] = []): string[] => {
    const seen = new Set<string>();
    const result: string[] = [];
    values.forEach((value) => {
        const formatted = toSchoolEntityId(value);
        if (formatted && !seen.has(formatted)) {
            seen.add(formatted);
            result.push(formatted);
        }
    });
    return result;
};

const extractErrorMessage = (body: any, fallback: NullableString = ''): string => {
    const errors = ApiErrors.extractAllErrors(body, undefined, fallback || '');
    return ApiErrors.formatErrors(errors);
};

const resolveDivisionId = (candidate?: number): number => {
    if (typeof candidate === 'number' && !Number.isNaN(candidate) && candidate > 0) {
        return candidate;
    }
    const stored = getSelectedDivisionId();
    return stored > 0 ? stored : 0;
};

const mapHolidayRecord = (dto: any): HolidayRecord => {
    return {
        id: dto?.id ?? '',
        name: dto?.name ?? '',
        dateFrom: dto?.dateFrom ?? dto?.startDate ?? '',
        dateTo: dto?.dateTo ?? dto?.endDate ?? '',
        schoolIds: normalizeSchoolIds(Array.isArray(dto?.schoolIds) ? dto.schoolIds : []),
    };
};

export interface GetSchoolHolidaysParams {
    divisionId?: number;
    schoolIds: readonly SchoolIdentifierInput[];
    year: number;
}

export async function getSchoolHolidays({ divisionId, schoolIds, year }: GetSchoolHolidaysParams): Promise<HolidayRecord[]> {
    const normalizedSchoolIds = normalizeSchoolIds(schoolIds);
    if (!normalizedSchoolIds.length) {
        return [];
    }
    const resolvedDivisionId = resolveDivisionId(divisionId);
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_manager_get.replace('{divisionId}', encodeURIComponent(String(resolvedDivisionId)));
    const query = new URLSearchParams();
    query.set('year', String(year));
    normalizedSchoolIds.forEach((id) => query.append('schoolIds', id));
    const url = `${endpoint}?${query.toString()}`;
    const getArray = (result: any) => {
        if (Array.isArray(result)) {
            return result;
        }
        if (Array.isArray(result?.holidays)) {
            return result.holidays;
        }
        return Array.isArray(result?.data) ? result.data : [];
    };
    return await localApi.fetchList(url, mapHolidayRecord, 'Failed to load holidays', getArray);
}

export interface SaveSchoolHolidaysRequest {
    divisionId?: number;
    year: number;
    schoolIds: readonly SchoolIdentifierInput[];
    newHolidays: readonly HolidayDraft[];
    deletedHolidayIds: readonly (string | number)[];
}

export interface SaveSchoolHolidaysResult {
    ok: boolean;
    message?: string;
    errors?: string[];
    payload?: any;
}

export async function saveSchoolHolidays({ divisionId, year, schoolIds, newHolidays, deletedHolidayIds }: SaveSchoolHolidaysRequest): Promise<SaveSchoolHolidaysResult> {
    const resolvedDivisionId = resolveDivisionId(divisionId);
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_manager_save.replace('{divisionId}', encodeURIComponent(String(resolvedDivisionId)));
    const payload = {
        year,
        schoolIds: normalizeSchoolIds(schoolIds),
        newHolidays: (newHolidays ?? []).map((holiday) => ({
            ...holiday,
            schoolIds: normalizeSchoolIds(holiday?.schoolIds ?? schoolIds),
        })),
        deletedHolidayIds: Array.isArray(deletedHolidayIds) ? deletedHolidayIds : [],
    };

    const response = await localApi.call(endpoint, 'POST', JSON.stringify(payload));
    if (!response) {
        return { ok: false, message: 'No response received from the server.' };
    }
    let body: any = null;
    try {
        body = await response.clone().json();
    } catch {
        body = null;
    }
    if (!response.ok) {
        const errors = ApiErrors.extractAllErrors(body, response, 'Failed to save holidays');
        return {
            ok: false,
            message: ApiErrors.formatErrors(errors),
            errors,
            payload: body,
        };
    }
    const errors = ApiErrors.extractAllErrors(body, response, '');
    return {
        ok: true,
        message: errors.length > 0 ? ApiErrors.formatErrors(errors) : (body?.message || 'Holidays saved successfully.'),
        payload: body,
    };
}

export interface CopySchoolHolidaysRequest {
    divisionId?: number;
    targetYear: number;
    schoolIds: readonly SchoolIdentifierInput[];
}

export async function copySchoolHolidays({ divisionId, targetYear, schoolIds }: CopySchoolHolidaysRequest): Promise<SaveSchoolHolidaysResult> {
    const resolvedDivisionId = resolveDivisionId(divisionId);
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_manager_copy.replace('{divisionId}', encodeURIComponent(String(resolvedDivisionId)));
    const payload = {
        targetYear,
        sourceYear: targetYear - 1,
        schoolIds: normalizeSchoolIds(schoolIds),
    };
    const response = await localApi.call(endpoint, 'POST', JSON.stringify(payload));
    if (!response) {
        return { ok: false, message: 'No response received from the server.' };
    }
    let body: any = null;
    try {
        body = await response.clone().json();
    } catch {
        body = null;
    }
    if (!response.ok) {
        const errors = ApiErrors.extractAllErrors(body, response, 'Failed to copy holidays');
        return {
            ok: false,
            message: ApiErrors.formatErrors(errors),
            errors,
            payload: body,
        };
    }
    const errors = ApiErrors.extractAllErrors(body, response, '');
    return {
        ok: true,
        message: errors.length > 0 ? ApiErrors.formatErrors(errors) : (body?.message || 'Holidays copied successfully.'),
        payload: body,
    };
}

export interface GetHolidaysByPricingYearParams {
    year: number;
    schoolIds: readonly SchoolIdentifierInput[];
}

export async function getHolidaysByPricingYear({ year, schoolIds }: GetHolidaysByPricingYearParams): Promise<HolidayRecord[]> {
    const normalizedSchoolIds = normalizeSchoolIds(schoolIds);
    if (!normalizedSchoolIds.length) {
        return [];
    }
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_get.replace('{year}', encodeURIComponent(String(year)));
    const schoolIdsParam = normalizedSchoolIds.join(',');
    const url = `${endpoint}?schoolIds=${encodeURIComponent(schoolIdsParam)}`;
    const getArray = (result: any) => {
        if (Array.isArray(result)) {
            return result;
        }
        if (Array.isArray(result?.holidays)) {
            return result.holidays;
        }
        return Array.isArray(result?.data) ? result.data : [];
    };
    return await localApi.fetchList(url, mapHolidayRecord, 'Failed to load holidays', getArray);
}

export interface CreateHolidaysRequest {
    year: number;
    holidays: readonly HolidayDraft[];
}

export interface CreateHolidaysResult {
    ok: boolean;
    message?: string;
    errors?: string[];
    payload?: any;
}

export async function createHolidays({ year, holidays }: CreateHolidaysRequest): Promise<CreateHolidaysResult> {
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_create.replace('{year}', encodeURIComponent(String(year)));
    const payload = {
        holidays: (holidays ?? []).map((holiday) => ({
            name: holiday.name,
            dateFrom: holiday.dateFrom,
            dateTo: holiday.dateTo,
            schoolIds: normalizeSchoolIds(holiday?.schoolIds ?? []),
        })),
    };

    const response = await localApi.call(endpoint, 'POST', JSON.stringify(payload));
    if (!response) {
        return { ok: false, message: 'No response received from the server.' };
    }
    let body: any = null;
    try {
        body = await response.clone().json();
    } catch {
        body = null;
    }
    if (!response.ok) {
        const errors = ApiErrors.extractAllErrors(body, response, 'Failed to create holidays');
        return {
            ok: false,
            message: ApiErrors.formatErrors(errors),
            errors,
            payload: body,
        };
    }
    const errors = ApiErrors.extractAllErrors(body, response, '');
    return {
        ok: true,
        message: errors.length > 0 ? ApiErrors.formatErrors(errors) : (body?.message || 'Holidays created successfully.'),
        payload: body,
    };
}

export interface DeleteHolidaysRequest {
    year: number;
    holidays: readonly HolidayDraft[];
}

export interface DeleteHolidaysResult {
    ok: boolean;
    message?: string;
    errors?: string[];
    payload?: any;
}

export async function deleteHolidays({ year, holidays }: DeleteHolidaysRequest): Promise<DeleteHolidaysResult> {
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_delete.replace('{year}', encodeURIComponent(String(year)));
    const payload = {
        holidays: (holidays ?? []).map((holiday) => ({
            name: holiday.name,
            dateFrom: holiday.dateFrom,
            dateTo: holiday.dateTo,
            schoolIds: normalizeSchoolIds(holiday?.schoolIds ?? []),
        })),
    };

    const response = await localApi.call(endpoint, 'DELETE', JSON.stringify(payload));
    if (!response) {
        return { ok: false, message: 'No response received from the server.' };
    }
    let body: any = null;
    try {
        body = await response.clone().json();
    } catch {
        body = null;
    }
    if (!response.ok) {
        const errors = ApiErrors.extractAllErrors(body, response, 'Failed to delete holidays');
        return {
            ok: false,
            message: ApiErrors.formatErrors(errors),
            errors,
            payload: body,
        };
    }
    const errors = ApiErrors.extractAllErrors(body, response, '');
    return {
        ok: true,
        message: errors.length > 0 ? ApiErrors.formatErrors(errors) : (body?.message || 'Holidays deleted successfully.'),
        payload: body,
    };
}

export interface CopyHolidaysRequest {
    sourcePricingYearId: number;
    targetPricingYearId: number;
    schoolIds: readonly SchoolIdentifierInput[];
}

export interface CopyHolidaysResult {
    ok: boolean;
    message?: string;
    errors?: string[];
    payload?: any;
}

export async function copyHolidays({ sourcePricingYearId, targetPricingYearId, schoolIds }: CopyHolidaysRequest): Promise<CopyHolidaysResult> {
    const localApi = await getLocalApi();
    const endpoint = apiConfig.holiday_copy;
    const payload = {
        sourcePricingYearId,
        targetPricingYearId,
        schoolIds: normalizeSchoolIds(schoolIds),
    };
    const response = await localApi.call(endpoint, 'POST', JSON.stringify(payload));
    if (!response) {
        return { ok: false, message: 'No response received from the server.' };
    }
    let body: any = null;
    try {
        body = await response.clone().json();
    } catch {
        body = null;
    }
    if (Array.isArray(body) && body.length > 0) {
        const hasErrorMessages = body.some((item: any) => 
            (item?.message && typeof item.message === 'string' && item.message.trim().length > 0) ||
            item?.error || item?.errorMessage
        );
        if (hasErrorMessages) {
            const extractedErrors = ApiErrors.extractAllErrors(body, response, 'Failed to copy holidays');
            return {
                ok: false,
                message: ApiErrors.formatErrors(extractedErrors),
                errors: extractedErrors,
                payload: body,
            };
        }
    }
    
    const errors = ApiErrors.extractAllErrors(body, response, 'Failed to copy holidays');
    
    if (!response.ok || errors.length > 0) {
        return {
            ok: false,
            message: ApiErrors.formatErrors(errors),
            errors,
            payload: body,
        };
    }
    return {
        ok: true,
        message: body?.message || 'Holidays copied successfully.',
        payload: body,
    };
}